<template>
  <div class="mb-0.5">
    <div class="chart-header flex justify-end items-center mb-0.5">
      <DateRange v-model="periodDate" />
      <div class="flex items-center gap-1"><span class="font-bold">Compare</span><el-switch v-model="compare" /></div>
      <DateRange v-if="compare" v-model="compareDate" :isRange="false" />
      <ElButton type="primary" size="medium" class="ml-2" plain @click="generateReport">Generate</ElButton>
      <ElButton v-if="campaign?.report?.id" class="ml-2" type="primary" size="medium" plain @click="downloadPdf">Save as PDF</ElButton>
    </div>

    <div v-show="!isEmptyDataset" v-loading="loading" class="bg-white rounded-md border border-grayNeutralLight relative">
      <div class="w-full border-b border-b-grayNeutral flex items-center rounded-t-md">
        <ActionCard v-for="item in filteredMetrics" :key="item.slug" :data="item" :currentMetric="currentMetric" @actionClick="changeSetData" />
      </div>

      <!-- Chart -->
      <div class="p-2" ref="metricsChart"></div>
    </div>
    <div
      v-if="isEmptyDataset"
      v-loading="loading"
      class="bg-white rounded-md border border-grayNeutralLight h-[452px] flex justify-center items-center"
    >
      NO DATA
    </div>
  </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty'
import dayjs from 'dayjs'
import ApexCharts from 'apexcharts'
import ActionCard from './ActionCard'
import DateRange from './DateRange'

export default {
  name: 'CampaignMetricsChart',
  components: { ActionCard, DateRange },
  props: {
    dataSetObj: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    dataSetComparedObj: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    period: {
      type: Object,
      required: true,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chart: null,
      currentMetric: 'impressions',
      periodDate: '',
      compareDate: '',
      compare: false,
      categories: [],
      saveAsPdfCta: false,
      metrics: [
        {
          name: 'Impressions',
          slug: 'impressions',
          metric: 0,
          tooltipValue: 0,
          diff: false,
          isPositive: false,
          type: 'number',
          icon: 'eyeImp',
        },
        {
          name: 'Reach',
          slug: 'unique_reach',
          metric: 0,
          tooltipValue: 0,
          diff: false,
          isPositive: false,
          type: 'number',
          icon: 'reach',
        },
        {
          name: 'Total Clicks',
          slug: 'clicks',
          metric: 0,
          tooltipValue: 0,
          diff: false,
          isPositive: false,
          type: 'number',
          icon: 'ctr',
        },
        {
          name: 'CTR',
          slug: 'ctr',
          metric: 0,
          tooltipValue: 0,
          diff: false,
          isPositive: false,
          type: 'percentage',
          icon: 'ctr',
        },
      ],
    }
  },
  created() {
    this.periodDate = [this.period?.start_date, this.period?.end_date]
    this.parseHeaderData()
  },
  watch: {
    filteredMetrics: {
      handler(newMetrics) {
        // If current metric is unique_reach but it's now hidden, switch to impressions
        const metricExists = newMetrics.some((m) => m.slug === this.currentMetric)
        if (!metricExists) {
          this.currentMetric = 'impressions'
        }
      },
      immediate: true,
    },
    compare(value) {
      if (!value) {
        this.compareDate = ''
        this.dataSetComparedObj.set = false
        this.metrics.map((item) => {
          item.diff = false
          item.isPositive = false
          return item
        })
      }
    },
    periodDate(newPeriodDate) {
      this.$emit('update:periodDate', { start_date: newPeriodDate[0], end_date: newPeriodDate[1] })
      this.parseHeaderData()
    },
    compareDate(newCompareDate) {
      if (newCompareDate === '') {
        return
      }
      const { start_date, end_date } = this.formatComparedDate(newCompareDate)
      this.$emit('update:compareDate', { start_date, end_date })
    },
    dataSetObj: {
      handler() {
        this.parseHeaderData()
        this.chart.updateSeries(this.myDataSet)
      },
      deep: true,
    },
    dataSetComparedObj: {
      handler() {
        this.parseHeaderData()
        this.comparedSetData()
        // this.chart.updateSeries(this.myDataSet)
      },
      deep: true,
    },
  },
  mounted() {
    this.initChart()
  },
  computed: {
    campaign() {
      return this.$store.getters['socialBoostCampaign/campaign']
    },
    filteredMetrics() {
      // Check if unique_reach total is non-zero
      const reachTotal = this.dataSetObj?.set?.total?.unique_reach?.value || 0

      // If reach is 0, filter it out
      if (reachTotal === 0) {
        return this.metrics.filter((m) => m.slug !== 'unique_reach')
      }

      return this.metrics
    },
    isEmptyDataset() {
      return isEmpty(this.dataSetObj.set.dataSet)
    },
    myDataSet() {
      if (this.isEmptyDataset) {
        return []
      }
      const data = this.dataSetObj?.set?.dataSet[this.currentMetric]
      return [
        {
          name: `${this.categories[0]} - ${this.categories[this.categories.length - 1]}`,
          data,
          color: '#1E90FF',
        },
      ]
    },
  },

  destroyed() {
    this.chart.destroy()
  },

  methods: {
    parseHeaderData() {
      const chartHeader = Object.keys(this.dataSetObj?.set?.total)
      chartHeader.forEach((item) => {
        const metric = this.metrics.find((dataItem) => dataItem.slug === item)
        metric.metric = this.dataSetObj?.set?.total[item]?.numbro
        metric.tooltipValue = this.dataSetObj?.set?.total[item]?.value

        if (this.dataSetComparedObj.set) {
          const calc = (this.dataSetObj?.set?.total[item]?.value * 100) / this.dataSetComparedObj?.set?.total[item]?.value - 100
          metric.diff = parseFloat(calc.toFixed(2))
          metric.isPositive = metric.diff > 0
        }
      })
      this.categories = this.dataSetObj?.xaxis[this.currentMetric]
    },
    changeSetData(value) {
      this.currentMetric = value
      if (this.compare) {
        this.comparedSetData()
      } else {
        this.categories = this.dataSetObj?.xaxis[value]
        this.chart.updateSeries(this.myDataSet)
      }
    },
    comparedSetData() {
      const data = this.dataSetObj?.set?.dataSet[this.currentMetric]
      const comparedData = !isEmpty(this.dataSetComparedObj?.set?.dataSet)
        ? this.dataSetComparedObj?.set?.dataSet[this.currentMetric]
        : false
      const { intervalDate } = this.formatComparedDate(this.compareDate)

      const myData = [
        {
          name: `${this.categories[0]} - ${this.categories[this.categories.length - 1]}`,
          data,
          color: '#1E90FF',
        },
      ]
      if (comparedData) {
        myData.push({
          name: intervalDate,
          data: [...comparedData],
          color: '#0c60b3',
        })
      } else if (this.compareDate) {
        this.$message({
          showClose: true,
          duration: 5500,
          message: `I couldn't find any data for comparison from the selected period ${intervalDate}.`,
          type: 'warning',
        })
      }

      this.chart.updateSeries(myData)
    },
    formatComparedDate(newCompareDate) {
      const compareDate = dayjs(newCompareDate)
      const start_date = compareDate.format('YYYY-MM-DDTHH:mm:ssZ[Z]')
      const end_date = compareDate.add(this.categories.length, 'day').format('YYYY-MM-DDTHH:mm:ssZ[Z]')
      const intervalDate = `${compareDate.format('D MMM')} - ${compareDate.add(this.categories.length, 'day').format('D MMM')}`
      return { start_date, end_date, intervalDate }
    },
    initChart() {
      const ctx = this.$refs.metricsChart

      var options = {
        chart: {
          type: 'line',
          id: 'mychart',
          height: 316,
          toolbar: {
            show: false,
          },
          selection: {
            enabled: false,
          },
          zoom: {
            enabled: false,
          },
        },
        stroke: {
          show: true,
          dashArray: [0, 8],
          width: 2,
        },
        legend: {
          show: false,
        },
        tooltip: {
          enabled: true,
        },
        markers: {
          size: 0,
        },
        series: this.myDataSet,
        xaxis: {
          categories: this.categories,
        },
        yaxis: {
          labels: {
            formatter: (value) => {
              if (value >= 1000) {
                return (value / 1000).toFixed(0) + 'k'
              }
              return value
            },
          },
        },
      }

      this.chart = new ApexCharts(ctx, options)

      this.chart.render()
    },
    async generateReport() {
      await this.axios.post(`/api/social-products/social-boost/generate-report/${this.campaign?.id}`)
      this.saveAsPdfCta = true
    },
    async downloadPdf() {
      const url = `/api/social-products/social-boost/download-report/${this.campaign.id}`
      const link = document.createElement('a')
      link.href = url
      link.click()
    },
  },
}
</script>
